import { AppIcon } from '@/components/AppIcon';
import { StatePill } from '@/components/hr/StatePill';
import { money, rate, shortDate } from '@/components/ui';
import { formatNumber } from '@/format';
import {
  PAY_METHOD_TEXT,
  WORKER_TYPE_TEXT,
  amountInWords,
  periodLabel,
  type PaymentMethod,
  type WorkerType,
} from '@/hooks/hr';
import { dayCount } from './payrollShared';

/**
 * Payslip shapes and the readable payslip panel shared by the pay run
 * workbench and My Pay. Pay figures are optional: the server leaves them out
 * for anyone without the salary permission, and money() shows a dash.
 */

export type LineKind = 'EARNING' | 'DEDUCTION' | 'EMPLOYER';

/** One row of a pay run's payslip grid. */
export interface SlipRow {
  id: string;
  employeeId: string;
  employeeCode: string;
  employeeName: string;
  employeeNameUr: string | null;
  designationName: string | null;
  farmCode: string | null;
  workerType: WorkerType;
  paymentMethod: PaymentMethod;
  payableDays: string;
  absentDays: string;
  overtimeHours: string;
  attendanceSource: 'DAILY' | 'OVERRIDE' | 'NONE';
  grossSalary?: string | null;
  totalDeductions?: string | null;
  netPayable?: string | null;
  employerCost?: string | null;
  warnings: string[];
  onHold: boolean;
  holdReason: string | null;
  paidOn: string | null;
  paidMethod: PaymentMethod | null;
  paidReference: string | null;
  remarks: string | null;
}

export interface PayslipLine {
  id: string;
  code: string;
  label: string;
  kind: LineKind;
  quantity: string | null;
  payRate?: string | null;
  payAmount?: string | null;
  isManual: boolean;
  note: string | null;
}

/** One payslip with every line, as GET /hr/payslips/:id returns it. */
export interface PayslipDetail extends SlipRow {
  runId: string;
  documentNo: string;
  runKind: 'REGULAR' | 'OFF_CYCLE';
  runStage: string;
  runTitle: string | null;
  periodStart: string | null;
  periodEnd: string | null;
  farm: string;
  departmentName: string | null;
  cnic?: string | null;
  payBankName?: string | null;
  payAccountTitle?: string | null;
  payIban?: string | null;
  walletProvider: string | null;
  walletNo?: string | null;
  monthlyGrossUsed?: string | null;
  dailyRateUsed?: string | null;
  periodDays: string;
  basisDays: string;
  presentDays: string;
  paidLeaveDays: string;
  unpaidLeaveDays: string;
  holidayDays: string;
  weeklyOffDays: string;
  unmarkedDays: string;
  advanceDeducted?: string | null;
  otherDeduction?: string | null;
  taxablePay?: string | null;
  taxDeducted?: string | null;
  shortfall?: string | null;
  lines: PayslipLine[];
  advancesOwed: Array<{ documentNo: string; kind: string; outstandingAmount?: string | null }>;
}

/** Adds up the amounts the caller can see; null when every one is hidden. */
function sumOf(lines: Array<{ payAmount?: string | null }>): string | null {
  const seen = lines.filter((l) => l.payAmount !== undefined && l.payAmount !== null);
  if (lines.length > 0 && seen.length === 0) return null;
  return seen.reduce((t, l) => t + Number(l.payAmount), 0).toFixed(2);
}

export interface LineView {
  id?: string;
  label: string;
  kind: string;
  quantity: string | null;
  payRate?: string | null;
  payAmount?: string | null;
  isManual?: boolean;
  note?: string | null;
}

/** Earnings, deductions or company costs, with "quantity × rate" where known. */
export function LinesTable({
  title,
  titleUr,
  lines,
  total,
  empty,
  onRemove,
}: {
  title: string;
  titleUr?: string;
  lines: LineView[];
  /** The total from the payslip itself; falls back to adding up the lines. */
  total?: string | null | undefined;
  empty: string;
  onRemove?: ((line: LineView) => void) | undefined;
}) {
  const shownTotal = total !== undefined ? total : sumOf(lines);
  return (
    <div className="tablewrap">
      <table className="table payslip__lines">
        <thead>
          <tr>
            <th>
              {title} {titleUr && <span className="urdu" lang="ur">{titleUr}</span>}
            </th>
            <th className="num">Days / hours × rate</th>
            <th className="num">Amount</th>
            {onRemove && <th aria-label="Remove" />}
          </tr>
        </thead>
        <tbody>
          {lines.length === 0 ? (
            <tr>
              <td colSpan={onRemove ? 4 : 3} className="dim">
                {empty}
              </td>
            </tr>
          ) : (
            lines.map((l, i) => (
              <tr key={l.id ?? i}>
                <td>
                  {l.label}
                  {l.isManual && <span className="pill payslip__typed">Typed in</span>}
                  {l.note && <div className="dim payslip__linenote">{l.note}</div>}
                </td>
                <td className="num dim">
                  {l.quantity !== null && l.quantity !== undefined
                    ? l.payRate
                      ? `${formatNumber(l.quantity)} × ${rate(l.payRate)}`
                      : formatNumber(l.quantity)
                    : ''}
                </td>
                <td className="num">{money(l.payAmount)}</td>
                {onRemove && (
                  <td>
                    {l.isManual && (
                      <button
                        type="button"
                        className="btn btn--sm btn--ghost btn--withicon"
                        onClick={() => onRemove(l)}
                        aria-label={`Remove ${l.label}`}
                      >
                        <AppIcon kind="remove" size={14} />
                        Remove
                      </button>
                    )}
                  </td>
                )}
              </tr>
            ))
          )}
        </tbody>
        <tfoot>
          <tr>
            <th colSpan={2}>Total</th>
            <th className="num">{money(shownTotal)}</th>
            {onRemove && <th />}
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

/** Net pay, large, with the amount in words the way a cheque writes it. */
export function NetPay({ net }: { net: string | null | undefined }) {
  return (
    <div className="payslip__netbox">
      <div className="payslip__net">
        <span>
          Net pay <span className="urdu" lang="ur">خالص تنخواہ</span>
        </span>
        <strong>{money(net)}</strong>
      </div>
      {net !== null && net !== undefined && <p className="payslip__words">{amountInWords(net)}</p>}
    </div>
  );
}

const SOURCE_TEXT = {
  DAILY: 'From daily attendance',
  OVERRIDE: 'From month totals typed in',
  NONE: 'No attendance recorded',
};

/**
 * The payslip as the person would read it: who, the days, what was earned
 * and taken off, what the company pays on top, net pay in words, how it is
 * paid, and anything worth a second look.
 */
export function PayslipPreview({
  slip: d,
  onRemoveLine,
}: {
  slip: PayslipDetail;
  /** Given only when typed-in lines may be removed (a draft run). */
  onRemoveLine?: ((line: LineView) => void) | undefined;
}) {
  const earnings = d.lines.filter((l) => l.kind === 'EARNING');
  const deductions = d.lines.filter((l) => l.kind === 'DEDUCTION');
  const employer = d.lines.filter((l) => l.kind === 'EMPLOYER');
  const period = d.periodStart && d.periodEnd ? periodLabel(d.periodStart, d.periodEnd) : '—';
  const owed = d.advancesOwed.filter((a) => a.outstandingAmount === undefined || Number(a.outstandingAmount) > 0);
  const account =
    d.paymentMethod === 'BANK'
      ? [d.payBankName, d.payAccountTitle, d.payIban].filter((x) => x !== undefined && x !== null).join(' · ') ||
        (d.payIban === undefined ? 'Hidden' : 'No bank details on file')
      : d.paymentMethod === 'WALLET'
        ? [d.walletProvider, d.walletNo].filter((x) => x !== undefined && x !== null).join(' · ') ||
          (d.walletNo === undefined ? 'Hidden' : 'No wallet details on file')
        : null;

  return (
    <div className="payslip stack">
      <div className="payslip__who">
        <div>
          <strong>{d.employeeName}</strong>{' '}
          {d.employeeNameUr && (
            <span className="urdu" lang="ur">
              {d.employeeNameUr}
            </span>
          )}
          <div className="dim">
            <span className="mono">{d.employeeCode}</span>
            {d.designationName ? ` · ${d.designationName}` : ''}
            {d.departmentName ? ` · ${d.departmentName}` : ''} · {d.farm}
          </div>
        </div>
        <div className="payslip__period">
          <strong>{period}</strong>
          <div className="dim">
            {d.documentNo}
            {d.runTitle ? ` — ${d.runTitle}` : ''} · {WORKER_TYPE_TEXT[d.workerType]}
          </div>
        </div>
      </div>

      {d.warnings.length > 0 && (
        <div className="payrun__alert payrun__alert--soft" role="note">
          <strong className="withicon">
            <AppIcon kind="attention" size={14} />
            Please check
          </strong>
          <ul>
            {d.warnings.map((w) => (
              <li key={w}>{w}</li>
            ))}
          </ul>
        </div>
      )}

      <section aria-label="Days">
        <h3 className="formsection">Days</h3>
        <dl className="detaillist payslip__days">
          <dt>Period</dt>
          <dd>
            {period} ({dayCount(d.periodDays)} days)
          </dd>
          <dt>Per-day basis</dt>
          <dd>{dayCount(d.basisDays)} days</dd>
          <dt>Present</dt>
          <dd>{dayCount(d.presentDays)}</dd>
          <dt>Absent</dt>
          <dd>{dayCount(d.absentDays)}</dd>
          <dt>Paid leave</dt>
          <dd>{dayCount(d.paidLeaveDays)}</dd>
          <dt>Unpaid leave</dt>
          <dd>{dayCount(d.unpaidLeaveDays)}</dd>
          <dt>Holidays</dt>
          <dd>{dayCount(d.holidayDays)}</dd>
          <dt>Weekly offs</dt>
          <dd>{dayCount(d.weeklyOffDays)}</dd>
          <dt>Not marked</dt>
          <dd>{dayCount(d.unmarkedDays)}</dd>
          <dt>Days paid</dt>
          <dd>
            <strong>{dayCount(d.payableDays)}</strong>
          </dd>
          <dt>Overtime</dt>
          <dd>{dayCount(d.overtimeHours)} hours</dd>
          <dt>Attendance</dt>
          <dd>{SOURCE_TEXT[d.attendanceSource]}</dd>
          <dt>{d.workerType === 'DAILY' ? 'Daily rate used' : 'Monthly pay used'}</dt>
          <dd>{money(d.workerType === 'DAILY' ? d.dailyRateUsed : d.monthlyGrossUsed)}</dd>
        </dl>
      </section>

      <div className="payslip__cols">
        <LinesTable
          title="Earnings"
          titleUr="آمدنی"
          lines={earnings}
          total={d.grossSalary}
          empty="No earnings."
          onRemove={onRemoveLine}
        />
        <LinesTable
          title="Deductions"
          titleUr="کٹوتیاں"
          lines={deductions}
          total={d.totalDeductions}
          empty="Nothing taken off."
          onRemove={onRemoveLine}
        />
      </div>

      <NetPay net={d.netPayable} />

      {d.shortfall !== undefined && d.shortfall !== null && Number(d.shortfall) > 0 && (
        <p className="muted">
          {money(d.shortfall)} could not be taken from this pay and moves to the next pay run.
        </p>
      )}

      {employer.length > 0 && (
        <section aria-label="Company costs">
          <h3 className="formsection">Company costs</h3>
          <p className="muted">Paid by the company on top of pay. Never taken from the person.</p>
          <LinesTable title="Paid by the company" lines={employer} total={d.employerCost} empty="None." />
        </section>
      )}

      <section aria-label="Payment">
        <h3 className="formsection">Payment</h3>
        <dl className="detaillist">
          <dt>Paid by</dt>
          <dd>
            {PAY_METHOD_TEXT[d.paymentMethod]}
            {account ? ` — ${account}` : ''}
          </dd>
          {d.cnic !== undefined && (
            <>
              <dt>CNIC</dt>
              <dd className="mono">{d.cnic ?? '—'}</dd>
            </>
          )}
          <dt>Paid</dt>
          <dd>
            {d.paidOn ? (
              <>
                <StatePill state="PAID" label={`Paid on ${shortDate(d.paidOn)}`} />
                {d.paidMethod ? ` · ${PAY_METHOD_TEXT[d.paidMethod]}` : ''}
                {d.paidReference ? ` · ${d.paidReference}` : ''}
              </>
            ) : d.onHold ? (
              <StatePill state="HELD" />
            ) : (
              <StatePill state="UNPAID" />
            )}
          </dd>
          {d.onHold && (
            <>
              <dt>Held because</dt>
              <dd>{d.holdReason ?? '—'}</dd>
            </>
          )}
          {d.remarks && (
            <>
              <dt>Remarks</dt>
              <dd>{d.remarks}</dd>
            </>
          )}
        </dl>
      </section>

      {owed.length > 0 && (
        <section aria-label="Advances still owed">
          <h3 className="formsection">Advances and loans still owed</h3>
          <ul className="stacklist">
            {owed.map((a) => (
              <li key={a.documentNo}>
                <span className="mono">{a.documentNo}</span> {a.kind === 'LOAN' ? 'Loan' : 'Advance'} —{' '}
                {money(a.outstandingAmount)}
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
